import { useState } from 'react'
import type { ReactElement } from 'react'
import { useTranslate } from '../lib/i18n-provider'
import { SPACE_KINDS, spaceLabel } from '../lib/spaces-client'
import type { Space, SpaceKind } from '../lib/spaces-client'

/**
 * De indeling van één woning: keuken, tuin, badkamer 1, badkamer 2.
 *
 * Dit scherm gaat over de kamers, niet over de foto's. Het indelen zelf gebeurt op
 * de tegel van de foto — daar is te zien wat er op staat, en dat is de enige plek
 * waar de keuze te maken valt zonder te raden.
 *
 * Twee dingen die de interface uitdrukkelijk zegt en niet aan de verhuurder overlaat
 * om te ontdekken:
 *
 *   * **hoeveel foto's er aan een ruimte hangen**, want een lege ruimte is meestal
 *     een vergissing en op een kanaal een lege kamer;
 *   * **wat er gebeurt bij verwijderen**. De foto's blijven staan en raken alleen
 *     hun indeling kwijt (`ON DELETE SET NULL`, migratie 0030). Wie dat niet weet,
 *     durft een indeling niet te herzien.
 */

export interface SpacesSectionProps {
  readonly spaces: readonly Space[]
  readonly busy: boolean
  readonly onCreate: (kind: SpaceKind, label: string) => void
  readonly onRename: (spaceId: string, label: string) => void
  readonly onDelete: (space: Space) => void
}

export function SpacesSection({
  spaces,
  busy,
  onCreate,
  onRename,
  onDelete,
}: SpacesSectionProps): ReactElement {
  const t = useTranslate()

  const [kind, setKind] = useState<SpaceKind>('bedroom')
  const [label, setLabel] = useState('')

  return (
    <section className="page__section" aria-labelledby="spaces-heading">
      <h2 className="page__sectionTitle" id="spaces-heading">
        {t('dashboard.spaces.title')}
      </h2>
      <p className="prose">{t('dashboard.spaces.intro')}</p>

      {spaces.length === 0 && (
        <p className="page__status" role="status">
          {t('dashboard.spaces.empty')}
        </p>
      )}

      {spaces.length > 0 && (
        <ul className="space-list">
          {spaces.map((space) => (
            <SpaceRow
              busy={busy}
              key={space.id}
              onDelete={onDelete}
              onRename={onRename}
              space={space}
              spaces={spaces}
            />
          ))}
        </ul>
      )}

      <form
        className="space-form"
        onSubmit={(event) => {
          event.preventDefault()
          onCreate(kind, label.trim())
          // De soort blijft staan: wie een tweede slaapkamer toevoegt, voegt er
          // meestal ook een derde toe. De naam wordt leeggemaakt, want die is per
          // ruimte anders.
          setLabel('')
        }}
      >
        <label className="field">
          <span className="field__label">{t('dashboard.spaces.kind.label')}</span>
          <select
            className="field__input"
            disabled={busy}
            onChange={(event) => {
              const next = SPACE_KINDS.find((candidate) => candidate === event.target.value)
              if (next !== undefined) {
                setKind(next)
              }
            }}
            value={kind}
          >
            {SPACE_KINDS.map((candidate) => (
              <option key={candidate} value={candidate}>
                {t(`dashboard.spaces.kind.${candidate}`)}
              </option>
            ))}
          </select>
        </label>

        <label className="field">
          <span className="field__label">{t('dashboard.spaces.label.label')}</span>
          <input
            className="field__input"
            disabled={busy}
            maxLength={100}
            onChange={(event) => {
              setLabel(event.target.value)
            }}
            placeholder={t('dashboard.spaces.label.placeholder')}
            type="text"
            value={label}
          />
        </label>

        <button className="button button--primary" disabled={busy} type="submit">
          {t('dashboard.spaces.action.add')}
        </button>
      </form>
    </section>
  )
}

interface SpaceRowProps {
  readonly space: Space
  readonly spaces: readonly Space[]
  readonly busy: boolean
  readonly onRename: (spaceId: string, label: string) => void
  readonly onDelete: (space: Space) => void
}

function SpaceRow({ space, spaces, busy, onRename, onDelete }: SpaceRowProps): ReactElement {
  const t = useTranslate()

  // Het veld houdt zijn eigen concept vast en stuurt pas bij het verlaten; zelfde
  // afweging als bij de alt-teksten op de fototegel.
  const [draft, setDraft] = useState(space.label)

  return (
    <li className="space-list__item">
      <span className="space-list__name">{spaceLabel(space, spaces, t)}</span>

      <label className="field field--inline">
        <span className="field__label">{t('dashboard.spaces.label.label')}</span>
        <input
          className="field__input"
          disabled={busy}
          maxLength={100}
          onBlur={() => {
            if (draft.trim() !== space.label) {
              onRename(space.id, draft.trim())
            }
          }}
          onChange={(event) => {
            setDraft(event.target.value)
          }}
          placeholder={t(`dashboard.spaces.kind.${space.kind}`)}
          type="text"
          value={draft}
        />
      </label>

      <span className="space-list__count">
        {t('dashboard.spaces.photoCount', { count: space.photoCount })}
      </span>

      <button
        className="button button--danger"
        disabled={busy}
        onClick={() => {
          onDelete(space)
        }}
        type="button"
      >
        {t('dashboard.spaces.action.remove')}
      </button>
    </li>
  )
}
